import { DatePicker, Descriptions } from 'antd';
import moment from 'moment';
import { FC, useEffect, useRef, useState } from 'react';
import { ChromePicker } from 'react-color';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import useNotification from '../../../hooks/useNotification';
import { IBannerDetail } from '../../../models/banner.model';
import Button from '../../Button';
import CustomTimePicker from '../../CustomTimePicker';
import ConfirmModal from '../../modals/ConfirmModal';
import Radio from '../../Radio';
import ShowMovieModal from './modals/ShowMovieModal';
import UploadImageModal from './modals/UploadImageModal';
import { YN } from '../../../models/common.api.model';
import useAuth from '../../../hooks/useAuth';

export interface IProps {
  data?: IBannerDetail;
  submit: (data: FormData) => void;
  remove?: () => void;
}

const Editor: FC<IProps> = ({ data, submit, remove }) => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const history = useHistory();
  const { infoNotification } = useNotification();

  const [title, setTitle] = useState('');
  const [srtDate, setSrtDate] = useState('');
  const [srtTime, setSrtTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [attachType, setAttachType] = useState('image');
  const [pcFile, setPcFile] = useState<File>();
  const [mobileFile, setMobileFile] = useState<File>();
  const [pcFileCode, setPcFileCode] = useState<number | undefined>();
  const [mobileFileCode, setMobileFileCode] = useState<number | undefined>();
  const [movieUrl, setMovieUrl] = useState<string | undefined>('');
  const [openYN, setOpenYN] = useState<YN>('N');
  const [backgroundColor, setBackgroundColor] = useState('');
  const [seq, setSeq] = useState('');
  const [url, setUrl] = useState<string | undefined>('');
  const [memo, setMemo] = useState<string | undefined>('');
  const [mode, setMode] = useState('create');
  const [showMovieVisible, setShowMovieVisible] = useState(false);
  const [confirmRemoveVisible, setConfirmRemoveVisible] = useState(false);
  const [uploadPcImageVisible, setUploadPcImageVisible] = useState(false);
  const [uploadMobileImageVisible, setUploadMobileImageVisible] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [buttonText, setButtonText] = useState(true);

  useEffect(() => {
    if (data) {
      setTitle(data.title);
      setSrtDate(data.srtDate);
      setSrtTime(data.srtTime);
      setEndDate(data.endDate);
      setEndTime(data.endTime);
      if (data.pcAttachFile) {
        setPcFileCode(data.pcAttachFile.attachFileCode);
      }
      if (data.moAttachFile) {
        setMobileFileCode(data.moAttachFile.attachFileCode);
      }
      setAttachType(data.bannerType === 'IM' ? 'image' : 'movie');
      setMovieUrl(data.movieUrl);
      setOpenYN(data.openYN);
      setBackgroundColor(data.backgroundColor);
      setSeq(String(data.seq));
      setUrl(data.url);
      setMemo(data.memo);
      setMode('read');
    } else {
      setSrtDate(moment().format('YYYYMMDD'));
      setSrtTime('00:00');
      setEndDate(moment().add(1, 'days').format('YYYYMMDD'));
      setEndTime('00:00');
    }
  }, [data]);
  
  useEffect(() =>{
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    }
  }, []);

  const handleClickOutside = (e: any) => {
    if (wrapperRef && !wrapperRef?.current?.contains(e.target)) {
      setShowColorPicker(false);
      setButtonText(true);
    }
  };

  const handleClick = () => {
    if (buttonText === false) {
      setShowColorPicker(false);
    } else {
      setShowColorPicker(true);
    }
    setButtonText(!buttonText);
  };

  const onSubmit = () => {
    if (!title) {
      return infoNotification(t('homepage.archive.message1'));
    }
    if (!srtDate || !srtTime) {
      return infoNotification(t('homepage.mainBanner.message1'));
    }
    if (!endDate || !endTime) {
      return infoNotification(t('homepage.mainBanner.message2'));
    }
    if (mode === 'create' &&
        attachType === 'image' &&
        !pcFile &&
        !mobileFile) {
      return infoNotification(t('homepage.mainBanner.message3'));
    }
    if (attachType === 'movie' && !movieUrl) {
      return infoNotification(t('homepage.mainBanner.message4'));
    }
    if (!seq) {
      return infoNotification(t('homepage.mainBanner.message5'));
    }
    if (!backgroundColor) {
      return infoNotification(t('homepage.mainBanner.message6'));
    }
    if (backgroundColor.length !== 6) {
      return infoNotification(t('homepage.mainBanner.message7'));
    }

    const form = new FormData();
    form.append('bannerGBN', 'MA');
    form.append('title', title);
    form.append('srtDate', srtDate);
    form.append('srtTime', srtTime);
    form.append('endDate', endDate);
    form.append('endTime', endTime);
    form.append('openYN', openYN);
    form.append('seq', seq);
    form.append('backgroundColor', backgroundColor);
    if (attachType === 'image') {
      form.append('bannerType', 'IM');
    } else {
      form.append('bannerType', 'MO');
    }

    if (pcFile) {
      form.append('pcAttachFile', pcFile);
    }
    if (mobileFile) {
      form.append('moAttachFile', mobileFile);
    }
    if (pcFileCode) {
      form.append('pcAttachFileCode', String(pcFileCode));
    }
    if (mobileFileCode) {
      form.append('moAttachFileCode', String(mobileFileCode));
    }
    if (mode === 'edit') {
      // PC용 첨부파일
      if (pcFile && pcFileCode) {
        form.append('pcAttachFileStatus', 'update');
      } else if (pcFile && !pcFileCode) {
        form.append('pcAttachFileStatus', 'create');
      }
      // 모바일용 첨부파일
      if (mobileFile && mobileFileCode) {
        form.append('moAttachFileStatus', 'update');
      } else if (mobileFile && !mobileFileCode) {
        form.append('mobileAttachFileStatus', 'create');
      }
    }
    if (movieUrl) {
      form.append('movieUrl', movieUrl);
    }
    if (url) {
      form.append('url', url);
    }
    if (memo) {
      form.append('memo', memo);
    }

    submit(form);
  };

  return (
    <div className='comm-table__area rowType'>
      <Descriptions bordered column={2} size="small">
        <Descriptions.Item label={`* ${t('common.column.title')}`} span={2}>
          <input
            type="text"
            className="form-control"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={mode === 'read'}
          />
        </Descriptions.Item>
        <Descriptions.Item label={`* ${t('homepage.mainBanner.column2')}`}>
          <div className='comm-gap'>
            <div className="date-picker__area">
              <DatePicker
                className="mr-1"
                value={srtDate ? moment(srtDate) : undefined}
                onChange={(e) => {
                  if (e === null) setSrtDate('');
                  else setSrtDate(moment(e).format('YYYYMMDD'));
                }}
                disabled={mode === 'read'}
              />
            </div>
            <div className="time-picker__area">
              <CustomTimePicker
                value={srtTime}
                onSelect={(value) => setSrtTime(moment(value).format('HH:mm'))}
                disabled={mode === 'read'}
              />
            </div>
          </div>
        </Descriptions.Item>
        <Descriptions.Item label={`* ${t('homepage.mainBanner.column3')}`}>
          <div className='comm-gap'>
            <div className="date-picker__area">
              <DatePicker
                className="mr-1"
                value={endDate ? moment(endDate) : undefined}
                onChange={(e) => {
                  if (e === null) setEndDate('');
                  else setEndDate(moment(e).format('YYYYMMDD'));
                }}
                disabled={mode === 'read'}
              />
            </div>
            <div className="time-picker__area">
              <CustomTimePicker
                value={endTime}
                onSelect={(value) => setEndTime(moment(value).format('HH:mm'))}
                disabled={mode === 'read'}
              />
            </div>
          </div>
        </Descriptions.Item>
        <Descriptions.Item label={`* ${t('homepage.mainBanner.column4')}`} span={2}>
          <Radio
            checked={attachType === 'image'}
            onChange={() => setAttachType('image')}
            text={t('homepage.mainBanner.option1')}
            disabled={mode === 'read'}
          />
          <Radio
            className="ml-4"
            checked={attachType === 'movie'}
            onChange={() => setAttachType('movie')}
            text={t('homepage.mainBanner.option2')}
            disabled={mode === 'read'}
          />
        </Descriptions.Item>
        {attachType === 'image' ? (
          <>
            <Descriptions.Item label={t('homepage.mainBanner.column5')}>
              {data?.pcAttachFile ? (
                <>
                  <a
                    className="download-link btn-text"
                    href={data.pcAttachFile.fileLocation}
                    download={data.pcAttachFile.originalName}
                  >
                    {data.pcAttachFile.originalName}
                  </a>
                  <br />
                </>
              ) : null}
              <div className="comm-btn__area">
                <Button
                  className="btn-basic mint-line"
                  onClick={() => setUploadPcImageVisible(true)}
                  text={t('homepage.mainBanner.button1')}
                  disabled={mode === 'read'}
                />
                <span className="ml-2">{pcFile?.name}</span>
              </div>
            </Descriptions.Item>
            <Descriptions.Item label={t('homepage.mainBanner.column6')}>
              {data?.moAttachFile ? (
                <>
                  <a
                    className="download-link btn-text"
                    href={data.moAttachFile.fileLocation}
                    download={data.moAttachFile.originalName}
                  >
                    {data.moAttachFile.originalName}
                  </a>
                  <br />
                </>
              ) : null}
              <div className="comm-btn__area">
                <Button
                  className="btn-basic mint-line"
                  onClick={() => setUploadMobileImageVisible(true)}
                  text={t('homepage.mainBanner.button1')}
                  disabled={mode === 'read'}
                />
                <span className="ml-2">{mobileFile?.name}</span>
              </div>
            </Descriptions.Item>
          </>
        ) : (
          <Descriptions.Item label={`* ${t('homepage.education.column4')}`} span={2}>
            {mode === 'read' ? (
              <div onClick={() => setShowMovieVisible(true)}>
                <input
                  type="text"
                  className="form-control pointer"
                  value={movieUrl}
                  disabled
                />
              </div>
            ) : (
              <input
                type="text"
                className="form-control"
                value={movieUrl}
                onChange={(e) => setMovieUrl(e.target.value)}
              />
            )}
          </Descriptions.Item>
        )}
        <Descriptions.Item label={`* ${t('homepage.mainBanner.column7')}`} span={2}>
          <div className="w-150p">
            <Radio
              checked={openYN === 'Y'}
              onChange={() => setOpenYN('Y')}
              text="Y"
            />
            <Radio
              className="ml-4"
              checked={openYN === 'N'}
              onChange={() => setOpenYN('N')}
              text="N"
            />
          </div>
        </Descriptions.Item>
        <Descriptions.Item label={`* ${t('homepage.mainBanner.column8')}`} span={2}>
          <div className="w-150p flex" ref={wrapperRef}>
            <input
              type="text"
              className="form-control"
              value={backgroundColor}
              onChange={(e) => setBackgroundColor(e.target.value)}
              disabled
            />
            {mode !== 'read'
              ? (
                <>
                  <div className="row comm-btn__area ml-2">
                    <Button className={(buttonText === true ? "mint" : "gray") + " btn-basic small"} text={buttonText === true ? t('homepage.mainBanner.1') : t('homepage.mainBanner.2')} onClick={handleClick} value={backgroundColor} disabled={mode === 'read'} />
                    {showColorPicker && (
                      <div>
                        <ChromePicker className="color-picker" color={backgroundColor} onChange={(color) => setBackgroundColor(color.hex.replace("#", ""))} disableAlpha={true} />
                      </div>
                    )}
                  </div>
                </>
              )
              : null
            }
          </div>
        </Descriptions.Item>
        <Descriptions.Item label={`* ${t('homepage.mainBanner.column9')}`} span={2}>
          <div className="flex align-items-center">
            <input
              type="number"
              className="form-control w-100p"
              value={seq}
              onChange={(e) => setSeq(e.target.value)}
              disabled={mode === 'read'}
            />
            <span className="ml-2">
              {`${t('homepage.mainBanner.text1')} (${t('homepage.mainBanner.text2')})`}
            </span>
          </div>
        </Descriptions.Item>
        <Descriptions.Item label={t('homepage.mainBanner.column10')} span={2}>
          <input
            type="text"
            className="form-control"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            disabled={mode === 'read'}
          />
        </Descriptions.Item>
        <Descriptions.Item label={t('homepage.mainBanner.column11')} span={2}>
          <textarea
            rows={3}
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            disabled={mode === 'read'}
          />
        </Descriptions.Item>
      </Descriptions>

      <div className="comm-bx__between">
        <div className="comm-btn__area">
          <Button
            className="btn-basic gray-line"
            onClick={() => history.push('/homepage/main-banner')}
            text={t('common.button.list')}
          />
        </div>
        <div className="comm-btn__area right">
          {mode === 'read' ? (
            <>
              <Button
                className="btn-basic gray"
                onClick={() => setConfirmRemoveVisible(true)}
                text={t('common.button.delete')}
                writeAuthYn={writeAuthYn}
              />
              <Button
                className="btn-basic pink-line"
                onClick={() => setMode('edit')}
                text={t('common.button.edit')}
                writeAuthYn={writeAuthYn}
              />
            </>
          ) : (
            <Button
              className="btn-basic pink"
              onClick={onSubmit}
              text={t('common.button.save')}
              writeAuthYn={writeAuthYn}
            />
          )}
        </div>
      </div>

      {showMovieVisible && (
        <ShowMovieModal
          visible={showMovieVisible}
          setVisible={setShowMovieVisible}
          movieUrl={movieUrl}
        />
      )}
      {uploadPcImageVisible && (
        <UploadImageModal
          visible={uploadPcImageVisible}
          setVisible={setUploadPcImageVisible}
          isMobile={false}
          file={pcFile}
          setFile={setPcFile}
        />
      )}
      {uploadMobileImageVisible && (
        <UploadImageModal
          visible={uploadMobileImageVisible}
          setVisible={setUploadMobileImageVisible}
          isMobile
          file={mobileFile}
          setFile={setMobileFile}
        />
      )}
      {confirmRemoveVisible && (
        <ConfirmModal
          visible={confirmRemoveVisible}
          setVisible={setConfirmRemoveVisible}
          onConfirm={() => {
            if (remove) remove();
          }}
          titleText={t('system.menu.title6')}
          messageText={t('system.menu.text3')}
          confirmText={t('common.option.yes')}
          cancelText={t('common.option.no')}
        />
      )}
    </div>
  );
};

export default Editor;
